import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

// 백엔드 API 기본 URL - 전역 상수로 정의
const BASE_URL = "https://att-api.bs-soft.co.kr";

// 오류 메시지 상수
const ERROR_MESSAGES = {
  NOT_REGISTERED: '등록되지 않은 교육생입니다.',
  ALREADY_ATTENDED: '이미 출석이 등록되었습니다.',
};

const StudentAttendance = () => {
  const { classId } = useParams();
  const [searchParams] = useSearchParams();
  const qrToken = searchParams.get('token');
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const [success, setSuccess] = useState(false);
  const [classInfo, setClassInfo] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });
  const [responseData, setResponseData] = useState(null); // 서버 응답 저장
  
  // 디버그 정보 표시
  const showDebugInfo = () => {
    setDebugInfo({
      classId,
      token: qrToken,
      apiUrl: `${BASE_URL}/api/qr-gps/classes/${classId}/validate-qr?token=${qrToken}`,
      fullUrl: window.location.href,
      timestamp: new Date().toISOString()
    });
  };
  
  // QR 코드 토큰 검증 및 강의 정보 가져오기
  useEffect(() => {
    const validateToken = async () => {
      try {
        console.log('QR 코드 검증 시작:', { classId, qrToken });
        
        // API 요청 시 CORS 문제 해결을 위한 설정
        const config = {
          params: { token: qrToken },
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          withCredentials: false
        };
        
        // 요청 경로 변경 - 유효성 확인 엔드포인트가 달라졌을 수 있음
        // 1. 첫 번째 방법 시도: /api/qr-gps/classes/{classId}/validate-qr
        let response;
        try {
          response = await axios.get(`${BASE_URL}/api/qr-gps/classes/${classId}/validate-qr`, config);
        } catch (firstErr) {
          console.warn('첫 번째 API 엔드포인트 실패:', firstErr);
          
          // 2. 두 번째 방법 시도: /api/classes/{classId}/validate-qr
          try {
            response = await axios.get(`${BASE_URL}/api/classes/${classId}/validate-qr`, config);
          } catch (secondErr) {
            console.warn('두 번째 API 엔드포인트 실패:', secondErr);
            
            // 3. 세 번째 방법 시도: /api/attendance/validate-qr
            try {
              response = await axios.post(`${BASE_URL}/api/attendance/validate-qr`, {
                classId,
                token: qrToken
              }, {
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                }
              });
            } catch (thirdErr) {
              console.warn('세 번째 API 엔드포인트 실패:', thirdErr);
              throw thirdErr; // 모든 방법 실패 시 마지막 오류 전달
            }
          }
        }
        
        console.log('QR 코드 검증 응답:', response.data);
        
        // 응답 형식에 따라 처리
        if (response.data && (response.data.name || response.data.className)) {
          // 강의 정보 저장 (응답 구조가 다를 수 있음)
          setClassInfo({
            name: response.data.name || response.data.className || '알 수 없는 강의',
            instructorName: response.data.instructorName || response.data.instructor?.name || '알 수 없는 강사'
          });
        } else {
          // 예상치 못한 응답 형식인 경우
          console.warn('예상치 못한 응답 형식:', response.data);
          setClassInfo({
            name: '알 수 없는 강의',
            instructorName: '알 수 없는 강사'
          });
        }
      } catch (err) {
        console.error('QR 코드 검증 오류:', err);
        
        // 상세한 오류 정보 저장
        const errorDetails = {
          message: err.message,
          status: err.response?.status,
          data: err.response?.data,
          headers: err.response?.headers,
          config: err.config
        };
        console.error('상세 오류 정보:', errorDetails);
        
        // 사용자에게 보여줄 오류 메시지
        let errorMessage = '유효하지 않은 QR 코드입니다. 다시 스캔해주세요.';
        
        if (err.response) {
          console.error('응답 오류 데이터:', err.response.data);
          errorMessage = err.response.data.message || errorMessage;
        }
        
        setError(errorMessage);
        
        // 디버그 정보 자동 표시
        setDebugInfo({
          classId,
          token: qrToken,
          apiUrl: `${BASE_URL}/api/qr-gps/classes/${classId}/validate-qr?token=${qrToken}`,
          fullUrl: window.location.href,
          timestamp: new Date().toISOString(),
          error: errorDetails
        });
      }
    };
    
    if (classId && qrToken) {
      validateToken();
    } else {
      setError('필요한 정보가 부족합니다. QR 코드를 다시 스캔해주세요.');
      
      // 디버그 정보 자동 표시
      setDebugInfo({
        classId,
        token: qrToken,
        fullUrl: window.location.href,
        timestamp: new Date().toISOString(),
        error: '파라미터 누락 (classId 또는 token)'
      });
    }
  }, [classId, qrToken]);
  
  // 입력 필드 변경 핸들러
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // 특정 오류 메시지 검사 및 사용자 친화적 메시지 반환
  const getReadableErrorMessage = (error, className) => {
    // 응답 데이터가 없는 경우
    if (!error.response || !error.response.data) {
      return error.message || '출석 등록 중 오류가 발생했습니다.';
    }
    
    const responseData = error.response.data;
    const errorMessage = responseData.message || '';
    
    // 인증 오류(401) 처리
    if (error.response.status === 401) {
      return '서버 연결에 문제가 있습니다. 나중에 다시 시도해 주세요.';
    }
    
    // 오류 메시지에서 특정 패턴 확인
    if (errorMessage.includes('already registered') || 
        errorMessage.includes('already attended') || 
        errorMessage.includes('이미 출석') || 
        errorMessage.includes('duplicate')) {
      return ERROR_MESSAGES.ALREADY_ATTENDED;
    }
    
    if (errorMessage.includes('not registered') || 
        errorMessage.includes('not found') || 
        errorMessage.includes('not enrolled') || 
        errorMessage.includes('not a student')) {
      return `${className || '해당 강의'}에 ${ERROR_MESSAGES.NOT_REGISTERED}`;
    }
    
    // 기본 오류 메시지 반환
    return errorMessage || '출석 등록 중 오류가 발생했습니다.';
  };
  
  // 출석 등록 제출 핸들러
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.phone.trim()) {
      setError('이름과 핸드폰 번호를 모두 입력해주세요.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // 출석 등록 요청 데이터
      const attendanceData = {
        classId,
        token: qrToken,
        name: formData.name,
        phone: formData.phone,
        timestamp: new Date().toISOString() // 현재 시간을 출석 시간으로 기록
      };
      
      console.log('출석 등록 요청 데이터:', attendanceData);
      
      // 단일 API 엔드포인트 사용 - 공식 public 엔드포인트만 호출
      const apiUrl = `${BASE_URL}/api/attendance/public/attendance`;
      console.log(`POST ${apiUrl} 시도 중...`);
      
      // 출석 등록 API 호출
      try {
        // 헤더에 QR 토큰 추가
        const headers = {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-QR-Token': qrToken
        };
        
        // URL 쿼리 파라미터에도 토큰 추가
        const url = `${apiUrl}?token=${qrToken}`;
        
        const response = await axios({
          method: 'post',
          url: url,
          data: attendanceData,
          headers: headers
        });
        
        console.log(`API 호출 성공:`, response.data);
        
        // 서버 응답 저장
        setResponseData(response.data);
        
        // 성공 상태 설정 - 중복 출석도 성공으로 처리
        setSuccess(true);
        
        // 폼 초기화
        setFormData({
          name: '',
          phone: ''
        });
      } catch (err) {
        console.error('API 호출 실패:', err);
        
        // 특정 오류 상황에 대한 처리
        if (err.response) {
          const statusCode = err.response.status;
          const errorData = err.response.data;
          
          // 이미 출석한 경우(409 Conflict) - 백엔드에서 정상 응답으로 처리되므로 이 코드는 실행되지 않음
          if ((statusCode === 409 || statusCode === 400) && 
              errorData.message && (
                errorData.message.includes('already') || 
                errorData.message.includes('duplicate') ||
                errorData.message.includes('이미 출석')
              )) {
            setError(ERROR_MESSAGES.ALREADY_ATTENDED);
            return;
          }
          
          // 등록되지 않은 학생인 경우(404 Not Found)
          if ((statusCode === 404 || statusCode === 400) && 
              errorData.message && (
                errorData.message.includes('not registered') || 
                errorData.message.includes('not found') ||
                errorData.message.includes('not enrolled') || 
                errorData.message.includes('not a student')
              )) {
            setError(`${classInfo?.name || '해당 강의'}에 ${ERROR_MESSAGES.NOT_REGISTERED}`);
            return;
          }
          
          // 기타 서버 오류 메시지 표시
          setError(errorData.message || '출석 등록 중 오류가 발생했습니다.');
        } else {
          // 네트워크 오류 등
          setError('서버 연결에 실패했습니다. 네트워크 연결을 확인해주세요.');
        }
        
        // 디버깅 정보 자동 표시
        setDebugInfo({
          classId,
          token: qrToken,
          apiUrl: apiUrl,
          requestData: attendanceData,
          timestamp: new Date().toISOString(),
          error: {
            message: err.message,
            status: err.response?.status,
            data: err.response?.data
          }
        });
      }
    } catch (err) {
      console.error('출석 등록 오류:', err);
      setError('출석 등록 중 예기치 않은 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // QR 코드 유효성 우회 (임시 해결책)
  const bypassValidation = () => {
    setError(null);
    setClassInfo({
      name: '임시 강의',
      instructorName: '임시 강사'
    });
  };
  
  // 성공 메시지 컴포넌트
  const SuccessMessage = () => {
    // 중복 출석 여부 (responseData에서 확인)
    const isDuplicate = responseData?.duplicate;
    
    // 출석 시간 표시를 위한 처리
    let attendanceTime;
    if (isDuplicate && responseData?.timestamp) {
      // 이미 출석한 경우 서버에서 받은 원래 출석 시간 사용
      try {
        // ISO 문자열 또는 날짜 객체를 시간 문자열로 변환
        const originalDate = new Date(responseData.timestamp);
        attendanceTime = originalDate.toLocaleTimeString('ko-KR', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false  // 24시간 형식
        });
      } catch (err) {
        // 날짜 변환 실패 시 원본 값 그대로 사용
        attendanceTime = responseData.timestamp;
      }
    } else {
      // 새로 출석한 경우 현재 시간 사용
      attendanceTime = new Date().toLocaleTimeString('ko-KR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false  // 24시간 형식
      });
    }
    
    return (
      <div className="bg-green-50 border border-green-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-green-800">출석 등록 완료</h3>
            <div className="mt-2 text-sm text-green-700">
              <p>
                {isDuplicate 
                  ? '이미 출석이 등록되어 있습니다.' 
                  : '성공적으로 출석이 등록되었습니다.'}
              </p>
              <p className="mt-2 text-xs">등록 시간: {attendanceTime}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              <p>{error}</p>
            </div>
            
            {/* 오류 해결 버튼들 */}
            <div className="mt-4 flex justify-center space-x-4">
              <button 
                onClick={showDebugInfo}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                문제 해결 정보 보기
              </button>
              
              <button 
                onClick={bypassValidation}
                className="text-sm text-indigo-600 hover:text-indigo-700"
              >
                출석 등록 계속하기
              </button>
            </div>
            
            {debugInfo && (
              <div className="mt-4 p-3 bg-gray-50 rounded text-xs font-mono text-gray-600 overflow-auto">
                <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          출석 등록
        </h2>
        {classInfo && (
          <p className="mt-2 text-center text-sm text-gray-600">
            {classInfo.name} ({classInfo.instructorName} 강사)
          </p>
        )}
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {success ? (
            <SuccessMessage />
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  이름
                </label>
                <div className="mt-1">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="홍길동"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  핸드폰 번호
                </label>
                <div className="mt-1">
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="010-1234-5678"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                    isSubmitting 
                      ? 'bg-indigo-400 cursor-not-allowed' 
                      : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                  }`}
                >
                  {isSubmitting ? '처리 중...' : '출석 등록'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentAttendance; 